// src/pages/PaymentSuccess.js
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Home, RefreshCw, FileText, Shield } from 'lucide-react';
import { apiRequest } from '../utils/api';

const SuccessContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  padding: 2rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const SuccessCard = styled.div`
  background: white;
  border-radius: 24px;
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #10b981 0%, #059669 100%);
  }
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1rem;
  letter-spacing: -0.025em;
`;

const Message = styled.p`
  color: #6b7280;
  font-size: 1.125rem;
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const TransactionDetails = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  color: #6b7280;
  font-weight: 500;
  font-size: 0.9375rem;
`;

const DetailValue = styled.span`
  color: #111827;
  font-weight: 600;
  font-size: 0.9375rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
`;

const ActionButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
`;

const PrimaryButton = styled(ActionButton)`
  background: linear-gradient(90deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  
  &:hover {
    background: linear-gradient(90deg, #1e3a8a 0%, #2563eb 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(30, 64, 175, 0.3);
  }
`;

const SecondaryButton = styled(ActionButton)`
  background: white;
  color: #1e40af;
  border: 2px solid #3b82f6;
  
  &:hover {
    background: #f8fafc;
    transform: translateY(-2px);
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 3rem;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const txnid = params.get('txnid');
        
        if (!txnid) {
          setError('No transaction ID found');
          setLoading(false);
          return;
        }

        const response = await apiRequest(`/api/payments/status/?txnid=${txnid}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response && response.ok) {
          const data = await response.json();
          if (data.success) {
            setTransaction(data.transaction);
          } else {
            setError(data.message || 'Failed to fetch transaction details');
          }
        } else {
          setError('Failed to fetch transaction details');
        }
      } catch (err) {
        console.error('Error fetching transaction:', err);
        setError('An error occurred while fetching transaction details');
      } finally {
        setLoading(false);
      }
    };

    fetchTransactionDetails();
  }, [location]);

  if (loading) {
    return (
      <SuccessContainer>
        <SuccessCard>
          <LoadingContainer>
            <LoadingSpinner />
            <Title>Processing Payment...</Title>
            <Message>Please wait while we verify your payment</Message>
          </LoadingContainer>
        </SuccessCard>
      </SuccessContainer>
    );
  }

  if (error) {
    return (
      <SuccessContainer>
        <SuccessCard>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 2rem',
              border: '2px solid #fecaca'
            }}>
              <Shield size={32} color="#dc2626" />
            </div>
            <Title>Payment Verification Failed</Title>
            <Message>{error}</Message>
            <ButtonGroup>
              <PrimaryButton to="/upgrade-plan">
                <RefreshCw size={20} />
                Try Again
              </PrimaryButton>
              <SecondaryButton to="/">
                <Home size={20} />
                Go to Dashboard
              </SecondaryButton>
            </ButtonGroup>
          </div>
        </SuccessCard>
      </SuccessContainer>
    );
  }

  return (
    <SuccessContainer>
      <SuccessCard>
        <SuccessIcon>
          <CheckCircle size={40} color="white" />
        </SuccessIcon>
        
        <Title>Payment Successful!</Title>
        <Message>
          Thank you for your purchase. Your scans have been added to your account and are ready to use.
        </Message>

        {transaction && (
          <TransactionDetails>
            <DetailRow>
              <DetailLabel>Transaction ID:</DetailLabel>
              <DetailValue>{transaction.txnid}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Amount Paid:</DetailLabel>
              <DetailValue>₹{parseFloat(transaction.amount).toFixed(2)}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Scans Purchased:</DetailLabel>
              <DetailValue>{transaction.scan_count} {transaction.scan_type} scan(s)</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Date:</DetailLabel>
              <DetailValue>
                {new Date(transaction.payment_date || transaction.created_at).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Status:</DetailLabel>
              <DetailValue style={{ color: '#059669', fontWeight: 700 }}>
                {transaction.payment_status === 'success' ? 'Completed' : transaction.payment_status}
              </DetailValue>
            </DetailRow>
          </TransactionDetails>
        )}

        <div style={{ 
          padding: '1rem',
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <p style={{ 
            color: '#065f46',
            fontSize: '0.9375rem',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            justifyContent: 'center'
          }}>
            <CheckCircle size={16} />
            <strong>Scans have been added to your account!</strong>
          </p>
          <p style={{ 
            color: '#059669',
            fontSize: '0.875rem',
            margin: '0.5rem 0 0 0',
            textAlign: 'center'
          }}>
            You can now start using your purchased scans immediately.
          </p>
        </div>

        <ButtonGroup>
          <PrimaryButton to="/autoscan">
            <FileText size={20} />
            Start New Scan
          </PrimaryButton>
          <SecondaryButton to="/">
            <Home size={20} />
            Go to Dashboard
          </SecondaryButton>
        </ButtonGroup>

        <div style={{ 
          marginTop: '2rem',
          paddingTop: '1rem',
          borderTop: '1px solid #e5e7eb'
        }}>
          <p style={{ 
            fontSize: '0.75rem',
            color: '#6b7280',
            margin: 0
          }}>
            A receipt has been sent to your email. For any questions, contact support@yourdomain.com
          </p>
        </div>
      </SuccessCard>
    </SuccessContainer>
  );
};

export default PaymentSuccess;